/**
 * Per-session mutex for E2EE ratchet-state operations.
 *
 * Without this, two tabs of the same browser profile (= same device_id, same
 * IndexedDB origin) can race on `loadSession → decrypt/encrypt → saveSession`,
 * each advancing the ratchet from a stale snapshot and overwriting the other's
 * save. The next legitimate decrypt then fails with
 *   `aes/gcm: invalid ghash tag`
 * because the chain-keys have diverged from the sender's state.
 *
 * Uses `navigator.locks` where available (all modern browsers including iOS
 * Safari 15.4+, Firefox 96+, Chrome 69+). Falls back to an in-process Promise
 * chain per key for environments without it (older Safari, jsdom, etc.) —
 * which still serialises within a single tab, just not across tabs.
 *
 * The lock key is scoped to a single peer device, so unrelated sessions are
 * not blocked: `(peerA, devA)` and `(peerB, devB)` proceed in parallel.
 */

const inProcessChain = new Map<string, Promise<unknown>>();

export async function withSessionLock<T>(
  peerId: string,
  peerDeviceId: string,
  fn: () => Promise<T>,
): Promise<T> {
  const key = `kp-crypto-session:${peerId}:${peerDeviceId}`;

  if (typeof navigator !== "undefined" && navigator.locks?.request) {
    return navigator.locks.request(key, { mode: "exclusive" }, () => fn());
  }

  const prev = inProcessChain.get(key) ?? Promise.resolve();
  const current = prev.catch(() => undefined).then(() => fn());
  inProcessChain.set(key, current);
  try {
    return await current;
  } finally {
    if (inProcessChain.get(key) === current) {
      inProcessChain.delete(key);
    }
  }
}
